import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  BarChart3,
  PieChart as PieChartIcon,
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  RefreshCw,
  Users,
  ShoppingCart,
  DollarSign,
  Package,
  Globe,
  Clock,
  Target,
  Zap,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  Settings,
  Share2,
  AlertCircle,
  CheckCircle,
  Percent,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { formatCurrency, formatDate } from '../lib/utils';
import { useCommissionSourceAnalytics } from '../hooks/useCommissionSourceAnalytics';
import { useDiscountCodePerformance } from '../hooks/useDiscountCodePerformance';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Dummy data for analytics
const dummyPerformanceMetrics = [
  {
    id: 'METRIC-001',
    name: 'Conversion Rate',
    value: 3.2,
    unit: '%',
    change: 0.4,
    trend: 'up',
    period: 'Last 30 days',
    target: 3.5,
    category: 'Sales',
  },
  {
    id: 'METRIC-002',
    name: 'Average Order Value',
    value: 67.89,
    unit: '$',
    change: -2.15,
    trend: 'down',
    period: 'Last 30 days',
    target: 75.00,
    category: 'Sales',
  },
  {
    id: 'METRIC-003',
    name: 'Customer Acquisition Cost',
    value: 24.50,
    unit: '$',
    change: 1.25,
    trend: 'up',
    period: 'Last 30 days',
    target: 20.00,
    category: 'Marketing',
  },
  {
    id: 'METRIC-004',
    name: 'Customer Lifetime Value',
    value: 245.67,
    unit: '$',
    change: 12.34,
    trend: 'up',
    period: 'Last 30 days',
    target: 250.00,
    category: 'Customer',
  },
  {
    id: 'METRIC-005',
    name: 'Cart Abandonment Rate',
    value: 68.5,
    unit: '%',
    change: -3.2,
    trend: 'down',
    period: 'Last 30 days',
    target: 65.0,
    category: 'Sales',
  },
  {
    id: 'METRIC-006',
    name: 'Return Customer Rate',
    value: 28.7,
    unit: '%',
    change: 2.1,
    trend: 'up',
    period: 'Last 30 days',
    target: 30.0,
    category: 'Customer',
  },
];

const dummyTrafficData = [
  { source: 'Organic Search', visitors: 2847, percentage: 42.3, change: 8.5 },
  { source: 'Direct', visitors: 1923, percentage: 28.6, change: -2.1 },
  { source: 'Social Media', visitors: 1156, percentage: 17.2, change: 15.3 },
  { source: 'Email Marketing', visitors: 534, percentage: 7.9, change: 4.7 },
  { source: 'Paid Ads', visitors: 267, percentage: 4.0, change: -12.8 },
];

const dummyTopProducts = [
  {
    id: 'PROD-001',
    name: 'Blue Mountain Coffee - Premium Blend',
    sales: 234,
    revenue: 5847.66,
    margin: 68.5,
    views: 3456,
    conversionRate: 6.8,
  },
  {
    id: 'PROD-002',
    name: 'Jerk Seasoning Blend',
    sales: 189,
    revenue: 1699.11,
    margin: 72.3,
    views: 2134,
    conversionRate: 8.9,
  },
  {
    id: 'PROD-003',
    name: 'Coconut Oil - Extra Virgin',
    sales: 156,
    revenue: 2650.44,
    margin: 65.2,
    views: 1876,
    conversionRate: 8.3,
  },
  {
    id: 'PROD-004',
    name: 'Plantain Chips - Sea Salt',
    sales: 298,
    revenue: 1784.02,
    margin: 58.7,
    views: 4123,
    conversionRate: 7.2,
  },
];

const dummyCustomReports = [
  {
    id: 'REPORT-001',
    name: 'Monthly Sales Performance',
    description: 'Comprehensive sales analysis with product breakdowns',
    type: 'sales',
    schedule: 'monthly',
    lastGenerated: new Date('2024-01-25T09:00:00'),
    nextScheduled: new Date('2024-02-25T09:00:00'),
    recipients: ['admin@jampackd.store', 'sales@jampackd.store'],
    status: 'active',
    format: 'PDF',
  },
  {
    id: 'REPORT-002',
    name: 'Customer Behavior Analysis',
    description: 'Deep dive into customer journey and purchasing patterns',
    type: 'customer',
    schedule: 'weekly',
    lastGenerated: new Date('2024-01-22T10:00:00'),
    nextScheduled: new Date('2024-01-29T10:00:00'),
    recipients: ['marketing@jampackd.store'],
    status: 'active',
    format: 'Excel',
  },
  {
    id: 'REPORT-003',
    name: 'Inventory Turnover Report',
    description: 'Stock movement and inventory optimization insights',
    type: 'inventory',
    schedule: 'bi-weekly',
    lastGenerated: new Date('2024-01-20T08:00:00'),
    nextScheduled: new Date('2024-02-03T08:00:00'),
    recipients: ['inventory@jampackd.store'],
    status: 'paused',
    format: 'PDF',
  },
  {
    id: 'REPORT-004',
    name: 'Financial Summary Dashboard',
    description: 'Revenue, expenses, and profit analysis',
    type: 'financial',
    schedule: 'daily',
    lastGenerated: new Date('2024-01-25T06:00:00'),
    nextScheduled: new Date('2024-01-26T06:00:00'),
    recipients: ['finance@jampackd.store', 'admin@jampackd.store'],
    status: 'active',
    format: 'Dashboard',
  },
];

const dummyRealtimeData = {
  activeUsers: 47,
  currentSessions: 23,
  pageViews: 156,
  conversionRate: 3.8,
  revenueToday: 1247.89,
  ordersToday: 18,
  topPage: '/products/blue-mountain-coffee',
  topProduct: 'Blue Mountain Coffee - Premium Blend',
};

export function Analytics() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('Last 30 Days');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Affiliate analytics hooks
  const {
    analytics: sourceAnalytics,
    loading: sourceAnalyticsLoading,
  } = useCommissionSourceAnalytics('last_30_days');
  
  const {
    performance: discountPerformance,
    loading: discountPerformanceLoading,
  } = useDiscountCodePerformance('last_30_days');

  const getMetricTrendConfig = (trend: string) => {
    switch (trend) {
      case 'up':
        return { color: 'text-green-600', icon: TrendingUp, bgColor: 'bg-green-100' };
      case 'down':
        return { color: 'text-red-600', icon: TrendingDown, bgColor: 'bg-red-100' };
      default:
        return { color: 'text-gray-600', icon: Activity, bgColor: 'bg-gray-100' };
    }
  };

  const getReportStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', label: 'Active' };
      case 'paused':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Paused' };
      case 'error':
        return { color: 'bg-red-100 text-red-800', label: 'Error' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Real-time Dashboard */}
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-jamaica-green" />
            Real-time Dashboard
            <Badge className="bg-green-100 text-green-800 ml-2">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
              Live
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <Users className="h-6 w-6 text-jamaica-green" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{dummyRealtimeData.activeUsers}</p>
              <p className="text-sm text-gray-600">Active Users</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{dummyRealtimeData.currentSessions}</p>
              <p className="text-sm text-gray-600">Sessions</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <DollarSign className="h-6 w-6 text-jamaica-green" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(dummyRealtimeData.revenueToday)}</p>
              <p className="text-sm text-gray-600">Revenue Today</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <ShoppingCart className="h-6 w-6 text-jamaica-yellow" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{dummyRealtimeData.ordersToday}</p>
              <p className="text-sm text-gray-600">Orders Today</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dummyPerformanceMetrics.map((metric, index) => {
          const trendConfig = getMetricTrendConfig(metric.trend);
          const TrendIcon = trendConfig.icon;
          const progressPercentage = (metric.value / metric.target) * 100;
          
          return (
            <motion.div
              key={metric.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="admin-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {metric.unit === '$' ? formatCurrency(metric.value) : `${metric.value}${metric.unit}`}
                      </p>
                    </div>
                    <div className={`h-12 w-12 ${trendConfig.bgColor} rounded-lg flex items-center justify-center`}>
                      <TrendIcon className={`h-6 w-6 ${trendConfig.color}`} />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Target: {metric.unit === '$' ? formatCurrency(metric.target) : `${metric.target}${metric.unit}`}</span>
                      <span className={trendConfig.color}>
                        {metric.change > 0 ? '+' : ''}{metric.change}{metric.unit}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          progressPercentage >= 100 ? 'bg-green-500' : 
                          progressPercentage >= 80 ? 'bg-jamaica-yellow' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500">{metric.period}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Charts Placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="admin-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-jamaica-green" />
              Revenue Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Revenue chart will be implemented with Recharts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
                              <PieChartIcon className="h-5 w-5 text-jamaica-green" />
              Traffic Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dummyTrafficData.map((source, index) => (
                <div key={source.source} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}
                    ></div>
                    <span className="text-sm font-medium text-gray-900">{source.source}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{source.visitors.toLocaleString()}</p>
                    <p className={`text-xs ${source.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {source.change > 0 ? '+' : ''}{source.change}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderProductAnalyticsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-jamaica-green" />
            Top Performing Products
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Product</TableHead>
                  <TableHead>Sales</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Margin</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Conversion Rate</TableHead>
                  <TableHead>Performance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyTopProducts.map((product, index) => (
                  <TableRow
                    key={product.id}
                    className="hover:bg-gray-50 transition-colors"
                    style={{
                      opacity: 0,
                      transform: 'translateX(-20px)',
                      animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                    }}
                  >
                    <TableCell>
                      <p className="font-medium text-gray-900">{product.name}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-gray-900">{product.sales}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-green-600">{formatCurrency(product.revenue)}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Percent className="w-3 h-3 text-gray-400" />
                        <span className="font-medium">{product.margin}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600">{product.views.toLocaleString()}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Target className="w-3 h-3 text-jamaica-green" />
                        <span className="font-medium text-jamaica-green">{product.conversionRate}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-full bg-gray-200 rounded-full h-2 max-w-[60px]">
                          <div 
                            className="bg-jamaica-green h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min((product.conversionRate / 10) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {product.conversionRate > 8 ? 'Excellent' : 
                           product.conversionRate > 6 ? 'Good' : 'Average'}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderAffiliateAnalyticsTab = () => (
    <div className="space-y-6">
      {/* Affiliate Commission Source Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Source Breakdown Chart */}
        <Card className="admin-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-jamaica-green" />
              Commission Source Analytics
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sourceAnalyticsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jamaica-green"></div>
              </div>
            ) : sourceAnalytics ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Referral Links</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(sourceAnalytics.linkStats.totalEarnings)}</p>
                    <p className="text-sm text-blue-700">{sourceAnalytics.linkStats.count} commissions</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">Discount Codes</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-900">{formatCurrency(sourceAnalytics.discountStats.totalEarnings)}</p>
                    <p className="text-sm text-purple-700">{sourceAnalytics.discountStats.count} commissions</p>
                  </div>
                </div>
                
                {/* Chart */}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Referral Links', value: sourceAnalytics.summary.linkEarningsPercentage },
                          { name: 'Discount Codes', value: sourceAnalytics.summary.discountEarningsPercentage }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        <Cell fill="#3B82F6" />
                        <Cell fill="#8B5CF6" />
                      </Pie>
                      <Tooltip formatter={(value: any) => [`${value.toFixed(1)}%`, 'Share']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        <Card className="admin-card">
          <CardHeader>
            <CardTitle>Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            {sourceAnalyticsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            ) : sourceAnalytics ? (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800">Avg. Link Commission</p>
                  <p className="text-lg font-bold text-blue-900">{formatCurrency(sourceAnalytics.linkStats.avgCommission)}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-800">Avg. Discount Commission</p>
                  <p className="text-lg font-bold text-purple-900">{formatCurrency(sourceAnalytics.discountStats.avgCommission)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-green-800">Total Earnings</p>
                  <p className="text-lg font-bold text-green-900">{formatCurrency(sourceAnalytics.summary.totalEarnings)}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Discount Codes */}
      {discountPerformance && discountPerformance.length > 0 && (
        <Card className="admin-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-jamaica-green" />
              Top Performing Affiliate Discount Codes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Code</TableHead>
                    <TableHead>Commissions</TableHead>
                    <TableHead>Total Earnings</TableHead>
                    <TableHead>Unique Affiliates</TableHead>
                    <TableHead>Avg. Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {discountPerformanceLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 5 }).map((_, cellIndex) => (
                          <TableCell key={cellIndex}>
                            <div className="h-4 bg-gray-200 rounded animate-pulse" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    discountPerformance.slice(0, 10).map((code) => (
                      <TableRow key={code.discountCodeId}>
                        <TableCell className="font-mono text-sm">{code.discountCodeId}</TableCell>
                        <TableCell>{code.commissionCount}</TableCell>
                        <TableCell className="font-semibold text-green-600">
                          {formatCurrency(code.totalEarnings)}
                        </TableCell>
                        <TableCell>{code.uniqueAffiliates}</TableCell>
                        <TableCell>{formatCurrency(code.avgCommission)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderReportsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-jamaica-green" />
            Custom Reports ({dummyCustomReports.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Report Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Last Generated</TableHead>
                  <TableHead>Next Scheduled</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyCustomReports.map((report, index) => {
                  const statusConfig = getReportStatusConfig(report.status);
                  
                  return (
                    <TableRow
                      key={report.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                      }}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-gray-900">{report.name}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{report.description}</p>
                          <Badge variant="outline" className="text-xs">
                            {report.format}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {report.type.charAt(0).toUpperCase() + report.type.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-600 capitalize">{report.schedule}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">{formatDate(report.lastGenerated)}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">{formatDate(report.nextScheduled)}</p>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-900">{report.recipients.length} recipients</p>
                          <p className="text-xs text-gray-500 line-clamp-1">
                            {report.recipients.join(', ')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              View Report
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="w-4 h-4 mr-2" />
                              Download Latest
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Generate Now
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Report
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Share2 className="w-4 h-4 mr-2" />
                              Share Report
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Report
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes fadeInSlide {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div className="space-y-6">
        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex justify-end"
        >
          <Button className="bg-jamaica-green hover:bg-jamaica-green/90 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Create Report
          </Button>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">$45,678.90</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">+12.5% from last month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Orders</p>
                  <p className="text-2xl font-bold text-blue-600">1,234</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">+8.2% from last month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">New Customers</p>
                  <p className="text-2xl font-bold text-jamaica-green">156</p>
                </div>
                <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-jamaica-green" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">+15.3% from last month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                  <p className="text-2xl font-bold text-jamaica-yellow">3.2%</p>
                </div>
                <div className="h-12 w-12 bg-jamaica-yellow/20 rounded-lg flex items-center justify-center">
                  <Target className="h-6 w-6 text-jamaica-yellow" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">+0.4% from last month</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Analytics Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white rounded-xl shadow-md border border-gray-100"
        >
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'products', label: 'Product Analytics', icon: Package },
                { id: 'affiliates', label: 'Affiliate Analytics', icon: Users },
                { id: 'reports', label: 'Custom Reports', icon: FileText },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === id
                      ? 'border-jamaica-green text-jamaica-green'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'products' && renderProductAnalyticsTab()}
            {activeTab === 'affiliates' && renderAffiliateAnalyticsTab()}
            {activeTab === 'reports' && renderReportsTab()}
          </div>
        </motion.div>
      </div>
    </>
  );
} 